import {
    MAX_STORED_SEGMENTS,
    POINTER_TIMEOUT_MS,
    STROKE_FADE_MS,
    STROKE_HOLD_MS
} from './constants';

/**
 * Live strokes and pointers. Kept out of redux on purpose: they arrive many
 * times a second while someone draws, only the overlay canvas reads them, and
 * every dispatch would re-run the large video's (expensive) selectors.
 *
 * A segment is { sharerId, senderId, points, color, name, at }: part of a
 * stroke drawn on sharerId's screen share, in coordinates normalized to that
 * share's video frame.
 */
const _segments = [];

/**
 * Last known pointer per drawing participant: { sharerId, x, y, name, color, at }.
 */
const _pointers = new Map();

const _listeners = new Set();

/**
 * Tells the subscribed overlays to repaint.
 *
 * @returns {void}
 */
function _changed() {
    _listeners.forEach(listener => listener());
}

/**
 * Drops segments and pointers that have fully faded.
 *
 * @param {number} now - Current time.
 * @returns {void}
 */
function _prune(now) {
    const segmentCutoff = now - STROKE_HOLD_MS - STROKE_FADE_MS;

    while (_segments.length && _segments[0].at < segmentCutoff) {
        _segments.shift();
    }
    _pointers.forEach((pointer, id) => {
        pointer.at < now - POINTER_TIMEOUT_MS && _pointers.delete(id);
    });
}

/**
 * Adds a stroke segment.
 *
 * @param {Object} segment - The segment, without its timestamp.
 * @returns {void}
 */
export function addSegment(segment) {
    const now = Date.now();

    _prune(now);
    _segments.push({
        ...segment,
        at: now
    });
    _segments.length > MAX_STORED_SEGMENTS && _segments.shift();
    _changed();
}

/**
 * Moves (or creates) the pointer of a drawing participant.
 *
 * @param {string} senderId - The drawing participant.
 * @param {Object} pointer - { sharerId, x, y, name, color }.
 * @returns {void}
 */
export function setPointer(senderId, pointer) {
    _pointers.set(senderId, {
        ...pointer,
        at: Date.now()
    });
    _changed();
}

/**
 * Removes the pointer of a participant.
 *
 * @param {string} senderId - The participant.
 * @returns {void}
 */
export function removePointer(senderId) {
    _pointers.delete(senderId) && _changed();
}

/**
 * Forgets everything drawn on one screen share, or on all of them.
 *
 * @param {string} [sharerId] - The sharer; all shares when omitted.
 * @returns {void}
 */
export function clearShare(sharerId) {
    for (let i = _segments.length - 1; i >= 0; i--) {
        if (!sharerId || _segments[i].sharerId === sharerId) {
            _segments.splice(i, 1);
        }
    }
    _pointers.forEach((pointer, id) => {
        (!sharerId || pointer.sharerId === sharerId) && _pointers.delete(id);
    });
    _changed();
}

/**
 * What is visible on one screen share right now, each entry with the opacity
 * it fades to. Someone whose pointer is gone still gets one at the end of their
 * latest stroke, so their name stays next to their drawing until it fades.
 *
 * @param {string} sharerId - The sharer.
 * @returns {{ segments: Array<Object>, pointers: Array<Object> }}
 */
export function getVisible(sharerId) {
    const now = Date.now();
    const segments = [];
    const pointers = [];
    const latest = new Map();

    _prune(now);
    _segments.forEach(segment => {
        if (segment.sharerId === sharerId) {
            const fade = (now - segment.at - STROKE_HOLD_MS) / STROKE_FADE_MS;
            const visible = {
                ...segment,
                alpha: 1 - Math.min(1, Math.max(0, fade))
            };

            segments.push(visible);
            latest.set(segment.senderId, visible);
        }
    });
    _pointers.forEach((pointer, senderId) => {
        if (pointer.sharerId === sharerId) {
            pointers.push({
                ...pointer,
                alpha: 1,
                senderId
            });
            latest.delete(senderId);
        }
    });
    latest.forEach(({ alpha, color, name, points, senderId }) => {
        const [ x, y ] = points[points.length - 1];

        pointers.push({
            alpha,
            color,
            name,
            senderId,
            x,
            y
        });
    });

    return {
        segments,
        pointers
    };
}

/**
 * Subscribes to changes.
 *
 * @param {Function} listener - Called after every change.
 * @returns {Function} Unsubscribes.
 */
export function subscribe(listener) {
    _listeners.add(listener);

    return () => _listeners.delete(listener);
}
